import EAButton from "components/common/EAButton";
import EAIcon from "components/common/EAIcon";
import i18next from "i18next";
import React, { useCallback, useContext, useEffect, useState } from "react";
import DossierModelDataTable from "./DossierModelDataTable";
import { getDossierModelTemplate } from "services/edge/DossierModelService";
import { Context } from "states/Store";
import { ERROR } from "services/common/Constants";
import DragAndDropComponent from "./DragAndDropComponent";
import useStyles from "./DossierModelTabStyle";
import moment from "moment";
import DossierModelCreateDialog from "./dialogs/DossierModelCreateDialog";
import { partition as lodashPartition} from "lodash";
import { getAccountDossierTypes } from "services/edge/AccountService";
import EAColorIcon from '../../../../components/common/EAColorIcon';
import { PrivilegeEnum, RoleBasedAccessControlContext } from '../../../../services/common/RolesUtils';

function DossierModelTab() {
  const { classes } = useStyles();
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const [state, dispatch] = useContext(Context);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [dossierModelList, setDossierModelList] = useState([]);

  const downloadTemplate = useCallback(() => {
    if (state.account) {

      (async function fetchData() {
        try {

          const resp = await getDossierModelTemplate(
            state.account.id,
            dispatch
          );
          let blob = new Blob([resp?.arrayBuffer]);
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          let dateStr = moment().format('YYYYMMDD');
          link.download = `${dateStr}_${state.account.name}_TEMPLATE_MODELES_DOSSIERS.xlsx`;
          link.click();
          link.remove();

        } catch (e) {
          if (!e?.message.messages?.error) {
            console.error(e);
            dispatch({
              type: "ALERT",
              alert: { type: ERROR, msg: "INTERNAL_ERROR" },
            });
          }
        }
      })();
    }
  }, [dispatch, state.account]);

  const fetchDossiersModelList = useCallback(async () => {
    try {
      
      const results = await getAccountDossierTypes(state.account.id, null);
      // Order results by Creation Date
      let dossierTypes = results.content.sort(
        (a, b) => new Date(b.creationDate) - new Date(a.creationDate)
      );
      setDossierModelList(
        dossierTypes.map((x) => {
          return {
            ...x,
            nbDocuments: x.requirements.length,
          };
        })
      );
      const sortedDossiersTypes = dossierTypes?.sort((a, b) => a.libelle?.toLowerCase()?.localeCompare(b.libelle?.toLowerCase()));
      const [activeTypes, inactiveTypes] = lodashPartition(sortedDossiersTypes, 'active');
      dispatch({ type: "DOSSIER_TYPES", dossierTypes: activeTypes });
      dispatch({ type: "INACTIVE_DOSSIER_TYPES", dossierTypes: inactiveTypes });
    } catch (error) {
      setDossierModelList([]);
    }
  }, [state.account, dispatch]);

  useEffect(() => {
    if (state.account) {
      fetchDossiersModelList();
    }
  }, [state.account, fetchDossiersModelList]);

  return (
    <div className={classes.root}>
      {/*--------------------*/}
      {/* Création de modèle */}
      {/*--------------------*/}
      <div className={classes.rootEdit}>

        {/* Modèle unitaire */}
        <div className={classes.uniqueModelWrapper}>
          <EAColorIcon icon="demandes/add_dossier_model_green" width={"60px"}/>
          <div className={classes.uniqueModelTitle} style={{ position: "relative", top: "-10px" }}> {i18next.t("demandes.dossierModel.createModel")} </div>
          <EAButton
            content={i18next.t("demandes.dossierModel.continue")}
            onClick={() => setIsCreateDialogOpen(true)}
            size="large"
            customStyle={{ width: "250px", fontSize: "16px", position: "relative", top: "-10px" }}
            disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_TYPE)}
          />
        </div>

        {/* Séparation */}
        <div className={classes.separator}>
          <div className={classes.separatorLine}/>
          <div>OU</div>
          <div className={classes.separatorLine}/>
        </div>

        {/* Modèle en masse */}
        <div className={classes.multipleModelAndImportWrapper}>
          <div className={classes.multipleModelWrapper}>
            <div className={classes.multipleModelTitle}> {i18next.t("demandes.dossierModel.createModelMulti.title")}</div>
            <div className={classes.multipleModelContent}>
              <DragAndDropComponent fetchDossierModelList={fetchDossiersModelList}/>
              <div>
                <div className={classes.multipleModelContentNoticeTitle}> {i18next.t("demandes.dossierModel.createModelMulti.requiredData")} </div>
                <div className={classes.multipleModelContentNoticeItem}>
                  <EAColorIcon style={{ width: "30px" }} icon="demandes/doc_green" />
                  <div>{i18next.t("demandes.dossierModel.createModelMulti.labelAndCodeDoc")}</div>
                </div>
                <div className={classes.multipleModelContentNoticeItem}>
                  <EAColorIcon style={{ width: "30px" }} icon="demandes/dossier_green" />
                  <div>{i18next.t("demandes.dossierModel.createModelMulti.labelAndCodeModel")}</div>
                </div>
                <div className={classes.multipleModelContentNoticeItem}>
                  <EAColorIcon style={{ width: "30px" }} icon="demandes/corresponding_green" />
                  <div style={{position: 'relative', top: "10px"}}>
                    <div>{i18next.t("demandes.dossierModel.createModelMulti.matchingDocModel")}</div>
                    <div className={classes.multipleModelContentNoticeItemSubTitle}>
                    <span>{i18next.t("demandes.dossierModel.createModelMulti.matchingDocModelCaption")}</span>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modèle d'import à télécharger */}
          <EAButton
            colorVariant="secondary_dark"
            onClick={() => downloadTemplate()}
            customStyle={{
              backgroundColor: "transparent",
              minHeight: 0,
              padding: 0,
              color: "rgba(83, 83, 98, 1)"
            }}
            disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_TYPE)}
            content={
              <>
                <EAIcon icon={"download_grey_darker"} />
                <span style={{textDecoration: "underline"}}>{i18next.t("demandes.dossierModel.downloadModel")}</span>
              </>
            }
          />
        </div>
      </div>

      {/*---------------------*/}
      {/* Tableau des modèles */}
      {/*---------------------*/}
      <div className={classes.rootDatatable}>
        {dossierModelList &&
          <DossierModelDataTable
            dossierModelList={dossierModelList}
            fetchDossierModelList={fetchDossiersModelList}
          />
        }
      </div>

      {/* PopUp : on create */}
      {isCreateDialogOpen && (
        <DossierModelCreateDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onValidateSuccess={() => {
            setIsCreateDialogOpen(false);
            fetchDossiersModelList();
          }}
        />
      )}
    </div>
  )
}

export default DossierModelTab;
